import type { ChangeEvent, CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import MuiCheckbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

// antd <Checkbox> -> MUI Checkbox (+ FormControlLabel when it has a label child).
// antd onChange(e) exposes e.target.checked, which MUI's onChange(e) also does,
// so the handler is compatible; children become the label.

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  // antd CheckboxChangeEvent ~ ChangeEvent with target.checked; kept loose so
  // call sites typed against antd's event still assign.
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: unknown) => void;
  disabled?: boolean;
  indeterminate?: boolean;
  value?: string | number;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  id?: string;
  "data-cy"?: string;
}

export const Checkbox = ({
  checked, defaultChecked, onChange, onClick, disabled, indeterminate, value, children, style, className,
  "data-cy": dataCy, "data-testid": dataTestid, id, ...rest
}: CheckboxProps & { "data-testid"?: string }) => {
  const box = (
    <MuiCheckbox
      checked={checked}
      defaultChecked={defaultChecked}
      onChange={onChange}
      onClick={onClick}
      disabled={disabled}
      indeterminate={indeterminate}
      value={value}
      size="small"
      // antd put data-cy/id on the <input>; route them there (mirrors the Input
      // wrapper) so `input[data-cy=...]`/`#id` selectors resolve. Cast because
      // MUI Checkbox's inputProps type omits the data-* index (data-* are valid DOM).
      inputProps={{ "data-cy": dataCy, "data-testid": dataTestid, id } as unknown as InputHTMLAttributes<HTMLInputElement>}
      {...rest}
    />
  );
  if (children === undefined || children === null) {
    return <span style={style} className={className}>{box}</span>;
  }
  return <FormControlLabel control={box} label={children} style={style} className={className} />;
};

export default Checkbox;
